import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useQuery, useQueryClient } from 'react-query';
import { CartService } from '../services/CartService';
import { WishlistService } from '../services/WishlistService';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationService from '../services/NotificationService';
import NotificationSettingsService from '../services/NotificationSettingsService';
import MessageService from '../services/MessageService';
import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot 
} from 'firebase/firestore';

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Container,
  Tooltip,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Menu as MenuIcon,
  ShoppingCart,
  Favorite as FavoriteIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  ShoppingBag,
  Settings,
  ExitToApp,
  Dashboard,
  Inventory,
  Person,
  Security,
  Help,
  Home as HomeIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Message as MessageIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationSettings, setNotificationSettings] = useState(null);

  // Get cart items count with optimized settings
  const { data: cart } = useQuery(
    'cart',
    () => CartService.getCart(),
    {
      enabled: !!currentUser,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      initialData: { items: [], totalItems: 0 }
    }
  );

  // Calculate totals with proper null checks
  const cartItemsCount = cart?.items?.reduce((total, item) => {
    return total + (item?.quantity || 0);
  }, 0) || 0;

  // Get wishlist count with optimized settings
  const { data: wishlist } = useQuery(
    'wishlist',
    () => WishlistService.getWishlist(),
    {
      enabled: !!currentUser,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      initialData: { items: [] }
    }
  );

  const wishlistItemsCount = wishlist?.items?.length || 0;

  // Listen for notifications
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // First get notification settings
    const getSettings = async () => {
      try {
        const settings = await NotificationSettingsService.getSettings();
        setNotificationSettings(settings);
      } catch (error) {
        console.error('Error fetching notification settings:', error);
      }
    };
    getSettings();

    // Create a safe query that won't pass undefined values
    const createSafeQuery = () => {
      const notificationsRef = collection(db, 'notifications');
      
      // Make sure currentUser.uid is defined before using it
      if (!currentUser?.uid) {
        return null;
      }
      
      return query(
        notificationsRef,
        where('receiverId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
    };

    const safeQuery = createSafeQuery();
    
    // Only proceed if we have a valid query
    if (!safeQuery) {
      return;
    }

    const unsubscribe = onSnapshot(safeQuery, (snapshot) => {
      const notificationData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setNotifications(notificationData);
      
      // Count unread notifications
      const unreadNotifications = notificationData.filter(notification => !notification.read);
      setUnreadCount(unreadNotifications.length);
    }, (error) => {
      console.error('Error fetching notifications:', error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Check if this type of notification is enabled
      if (!notificationSettings?.notifications?.enabled) {
        console.log('Notifications are disabled');
        return;
      }

      // Type-specific checks
      switch (notification.type) {
        case 'message':
          if (!notificationSettings.notifications.messages) {
            console.log('Message notifications are disabled');
            return;
          }
          break;
        case 'order':
          if (!notificationSettings.notifications.orders) {
            console.log('Order notifications are disabled');
            return;
          }
          break;
        default:
          return;
      }

      // Close notification menu first
      setNotificationAnchor(null);

      // Mark as read
      await NotificationService.markAsRead(notification.id);

      // Force a small delay before navigation to ensure menu is closed
      setTimeout(() => {
        // Navigate based on type
        switch (notification.type) {
          case 'message':
            navigate('/profile', { 
              state: { 
                tabValue: 1,  // Messages tab
                from: 'notification'
              },
              replace: true
            });
            break;
          case 'order':
            navigate('/profile', { 
              state: { 
                tabValue: 2,  // Orders tab
                from: 'notification',
                orderTabValue: 1  // Received orders tab
              },
              replace: true
            });
            break;
          default:
            navigate('/profile', { replace: true });
        }
      }, 100);
    } catch (error) {
      console.error('Error handling notification click:', error);
      enqueueSnackbar(t('errors.notificationError'), { variant: 'error' });
    }
  };

  const getNotificationIcon = (type) => {
    // Only show icons for enabled notification types
    if (!notificationSettings?.notifications?.enabled || 
        !notificationSettings?.notifications?.[type]) {
      return null;
    }

    switch (type) {
      case 'message':
        return <MessageIcon />;
      case 'order':
        return <ShoppingCartIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const handleLogout = async () => {
    try {
      setAnchorEl(null); // Close the menu first
      await logout(); // Call the logout function
      navigate('/auth'); // Navigate to auth page
      enqueueSnackbar(t('common.logoutSuccess'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('common.logoutError'), { variant: 'error' });
      console.error('Logout error:', error);
    }
  };

  // Update the formatTimeAgo function to handle different timestamp formats
  const formatTimeAgo = (timestamp, t) => {
    if (!timestamp) {
      return t('notifications.timeAgo.justNow');
    }
    
    // Convert timestamp to Date object if it's not already
    let date;
    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      // Firestore Timestamp object
      date = timestamp.toDate();
    } else if (timestamp.seconds && timestamp.nanoseconds) {
      // Firestore Timestamp-like object
      date = new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000);
    } else if (typeof timestamp === 'number') {
      // Unix timestamp in milliseconds
      date = new Date(timestamp);
    } else {
      // Fallback
      console.warn('Unknown timestamp format:', timestamp);
      return t('notifications.timeAgo.justNow');
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return t('notifications.timeAgo.justNow');
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return t('notifications.timeAgo.minutes', { count: diffInMinutes });
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return t('notifications.timeAgo.hours', { count: diffInHours });
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    return t('notifications.timeAgo.days', { count: diffInDays });
  };

  return (
    <AppBar position="fixed" color="default" elevation={1}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
          >
            {t('common.appName')}
          </Typography>

          <Box 
            sx={{ 
              flexGrow: 1, 
              display: { xs: 'none', md: 'flex' }, 
              gap: 2,
              ml: 3
            }}
          >
            <Button
              color="inherit"
              onClick={() => navigate('/')}
              startIcon={<HomeIcon />}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              {t('common.home')}
            </Button>

            <Button
              color="inherit"
              onClick={() => navigate('/search')}
              startIcon={<SearchIcon />}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              {t('common.search')}
            </Button>

            {currentUser && (
              <Button
                color="primary"
                variant="contained"
                onClick={() => navigate('/add-product')}
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  boxShadow: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
              >
                {t('product.addNew')}
              </Button>
            )}
          </Box>

          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
            <LanguageSwitcher />
            
            {currentUser ? (
              <>
                <Tooltip title={t('common.wishlist')}>
                  <IconButton onClick={() => navigate('/wishlist')}>
                    <Badge badgeContent={wishlistItemsCount} color="error" showZero={false}>
                      <FavoriteIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                <Tooltip title={t('common.cart')}>
                  <IconButton onClick={() => navigate('/cart')}>
                    <Badge 
                      badgeContent={cartItemsCount} 
                      color="error"
                      showZero={false}
                    >
                      <ShoppingCart />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {notificationSettings?.notifications?.enabled && (
                  <Tooltip title={t('common.notifications')}>
                    <IconButton onClick={(e) => setNotificationAnchor(e.currentTarget)}>
                      <Badge 
                        badgeContent={unreadCount} 
                        color="error"
                        showZero={false}
                      >
                        <NotificationsIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title={t('common.profile')}>
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <Avatar 
                      src={currentUser.photoURL} 
                      alt={currentUser.displayName}
                      sx={{ width: 32, height: 32 }}
                    >
                      {currentUser.displayName?.[0]}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Button 
                color="inherit" 
                onClick={() => navigate('/auth')}
              >
                {t('auth.signIn')}
              </Button>
            )}
          </Box>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            onClick={() => setAnchorEl(null)} // Close menu on any click
            PaperProps={{
              elevation: 3,
              sx: {
                width: 320,
                mt: 1.5,
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* User Info Section */}
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar 
                  src={currentUser?.photoURL}
                  sx={{ width: 40, height: 40, mr: 1.5 }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {currentUser?.name || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentUser?.email}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Main Menu Items */}
            <MenuItem 
              onClick={() => {
                navigate('/profile');
                setAnchorEl(null);
              }}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                <Person fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary={t('common.profile')}
                secondary={t('common.manageProfile')}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </MenuItem>

            <Divider />

            {/* Logout */}
            <MenuItem 
              onClick={handleLogout}
              sx={{ 
                py: 1.5,
                color: 'error.main',
                '&:hover': { bgcolor: 'error.lighter' }
              }}
            >
              <ListItemIcon>
                <ExitToApp fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText 
                primary={t('common.logout')}
                secondary={t('common.signOut')}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </MenuItem>
          </Menu>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={() => setNotificationAnchor(null)}
            PaperProps={{
              sx: {
                width: 360,
                maxHeight: '80vh',
                overflowY: 'auto'
              }
            }}
          >
            {notifications?.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {t('notifications.empty')}
                </Typography>
              </Box>
            ) : (
              notifications?.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 2,
                    px: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    },
                    opacity: notification.read ? 0.7 : 1
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    width: '100%',
                    minWidth: 0 // Add this to allow text truncation
                  }}>
                    {/* Avatar/Icon */}
                    <Avatar
                      src={notification.type === 'message' ? notification.senderPhoto : null}
                      sx={{
                        bgcolor: notification.type === 'message' ? 'primary.main' : 'success.main',
                        flexShrink: 0 // Prevent avatar from shrinking
                      }}
                    >
                      {notification.type === 'message' ? (
                        <MessageIcon fontSize="small" />
                      ) : (
                        <ShoppingCartIcon fontSize="small" />
                      )}
                    </Avatar>

                    {/* Content */}
                    <Box sx={{ 
                      flexGrow: 1,
                      minWidth: 0 // Add this to allow text truncation
                    }}>
                      {/* Title */}
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 0.5,
                          fontWeight: notification.read ? 'regular' : 'medium',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {notification.type === 'message' 
                          ? t('notifications.messages.new', { sender: notification.senderName })
                          : t('notifications.orders.new', { buyer: notification.buyerName })
                        }
                      </Typography>

                      {/* Preview */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.3,
                          maxHeight: '2.6em'
                        }}
                      >
                        {notification.type === 'message'
                          ? t('notifications.messages.preview', { message: notification.message })
                          : notification.message // Use formatted message directly
                        }
                      </Typography>

                      {/* Time */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ 
                          mt: 0.5, 
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {formatTimeAgo(notification.createdAt, t)}
                      </Typography>
                    </Box>

                    {/* Unread indicator */}
                    {!notification.read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          alignSelf: 'center',
                          flexShrink: 0 // Prevent dot from shrinking
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
} 